/**
 * sl-m82f.4.1: Shared formatting helpers for the analytics view.
 *
 * Centralized here so tab components and the orchestrator stay DRY — every
 * credit number in the UI is formatted through the same pipeline.
 */

const MICRO_PER_CREDIT = 10_000;

/**
 * Map internal provider IDs (e.g. `anthropic-bedrock`) to user-facing
 * display names. Extracted from the legacy usage-analytics component.
 */
const PROVIDER_DISPLAY_NAMES: Record<string, string> = {
  'anthropic-bedrock': 'Anthropic',
  'anthropic-direct': 'Anthropic',
  anthropic: 'Anthropic',
  'google-vertex': 'Google',
  'google-direct': 'Google',
  google: 'Google',
  'openai-direct': 'OpenAI',
  openai: 'OpenAI',
};

/**
 * Convert a bigint-as-string micro-credit value to display credits.
 * Used to parse `FeatureTotalsResponse` fields which are strings to survive
 * JSON (backend serializes `BigInt` via `.toString()`).
 */
export function microStringToCredits(microStr: string): number {
  if (!microStr) return 0;
  // `Number(big)` is safe here — we only display values up to a few million
  // credits, well inside JS's safe integer range.
  return Number(BigInt(microStr)) / MICRO_PER_CREDIT;
}

/** Format display credits for UI. Internal arithmetic stays in raw values. */
export function formatCredits(value: number): string {
  if (!Number.isFinite(value)) return '0.00';
  return value.toFixed(2);
}

/** Render an integer count with thousand separators. */
export function formatCount(value: number): string {
  return value.toLocaleString();
}

/** Format an ISO date string as a short locale date (e.g. "Apr 8, 2026"). */
export function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function capitalizeProvider(provider: string): string {
  if (!provider) return '';
  return (
    PROVIDER_DISPLAY_NAMES[provider] ??
    PROVIDER_DISPLAY_NAMES[provider.toLowerCase()] ??
    provider.charAt(0).toUpperCase() + provider.slice(1)
  );
}

export function formatTopModelLabel(model: string): string {
  const slashIndex = model.indexOf('/');
  if (slashIndex <= 0) return model;
  const provider = model.slice(0, slashIndex);
  return `${model} (${capitalizeProvider(provider)})`;
}

/** Truncate a string with an ellipsis if it exceeds the given length. */
export function truncate(value: string, max: number): string {
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1)}…`;
}
